import "./style.scss";
import "components/shared/DetailTabs/style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { Tab, Tooltip } from "@mui/material";
import { artworkNatureList } from "artworkNatureList";
import Edit from "assets/img/svg/edit";
import Plus from "assets/img/svg/plus";
import TabCountLabel from "components/LoadingDots/TabCountLabel";
import DeleteModal from "components/shared/DeleteModal";
import EmptyState from "components/shared/EmptyState";
import FilterInput from "components/shared/FilterInput";
import ResponsiveButton from "components/shared/ResponsiveStyledButton";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import TextSkeleton from "components/shared/TextSkeleton";
import TitleCover from "components/shared/TitleCover";
import TitlesTerm from "components/shared/TitlesTerms";
import { filterTitlesByQuery } from "containers/TermFormContainer/CoveredTitlesField/titleDisplay";
import type { ITitle } from "containers/Titles/types";
import type { IWork } from "containers/Works/types";
import { coverUrlForWork } from "containers/Works/workPreview";
import { useCompanySettings } from "hooks/useCompanySettings";
import useDetailTabSwitch, { getDetailTabPanelContentKey } from "hooks/useDetailTabSwitch";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTitleGroups, removeTitlesByIds } from "store/titleStore";
import { deleteWork, fetchWorkTerms } from "store/workStore";

import NewWorkModal from "../NewWorkModal";
import WorkTitleCard from "../WorkTitleCard";

type WorkDetailProps = {
    work?: IWork | null;
    onAddTitle: (workId: number) => void;
    onAddContract: (workId: number) => void;
    onOpenTitle: (titleId: number) => void;
    onDeleted?: () => void;
    canAddTitle?: boolean;
    canAddContract?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
    isLoading?: boolean;
};

const WorkDetail = ({
    work,
    onAddTitle,
    onAddContract,
    onOpenTitle,
    onDeleted,
    canAddTitle = true,
    canAddContract = true,
    canUpdate = false,
    canDelete = false,
    isLoading = false,
}: WorkDetailProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { getCompanySettings, countryFeatures } = useCompanySettings();
    const usesMultiPeriod = getCompanySettings("uses_simultaneous_period");
    const usesTitleGroups = getCompanySettings("uses_intertitle_compensation") === true;
    const { workTerms, workTermsStatus, workTermsWorkId } = useAppSelector((state) => state.works);
    const { titleGroups } = useAppSelector((state) => state.titles);
    const requestedTab = location.state != null && location.state.tab ? String(location.state.tab) : "1";
    const { value, tabEnterKey, handleTabChange } = useDetailTabSwitch(requestedTab);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [articleQuery, setArticleQuery] = useState("");
    const titles = work?.titles ?? [];
    const titleCount = titles.length;
    const showData = !isLoading && work != null;
    const filteredTitles = useMemo(
        () => filterTitlesByQuery(work?.titles ?? [], articleQuery),
        [work?.titles, articleQuery],
    );
    const hasArticleQuery = articleQuery.trim().length > 0;
    const showTitleSearch = showData && titleCount > 0;
    const usesMultipleBrands = getCompanySettings("uses_multiple_brands") === true;
    const artworkNatureLabel = artworkNatureList.find((item) => item.code === work?.artworkNature)?.description;
    const compensationGroupName = titleGroups.find((group) => group.id === work?.compensationGroupId)?.name;
    const showArtworkNature = showData && countryFeatures?.urssaf === true && artworkNatureLabel != null;
    const showCompensationGroup = showData && usesTitleGroups && compensationGroupName != null;
    const workCode = work?.workCode?.trim() ?? "";
    const showWorkCode = showData && workCode.length > 0;
    const analysisCode = work?.analysisCode?.trim() ?? "";
    const showAnalysisCode = showData && analysisCode.length > 0;
    const associatedBrand = work?.associatedBrand?.trim() ?? "";
    const showAssociatedBrand = showData && usesMultipleBrands && associatedBrand.length > 0;
    const firstTitle = titles[0];
    const titleForTerms = {
        id: firstTitle?.id ?? 0,
        name: work?.name ?? "",
        ean13: firstTitle?.ean13 ?? "",
        workId: work?.id ?? 0,
    } as ITitle;
    const termsBelongToWork = workTermsWorkId === work?.id;
    const hasSettledTerms = termsBelongToWork && workTermsStatus === "succeeded";
    const termsFailed = termsBelongToWork && workTermsStatus === "failed";
    // Previous rows stay in the store during a refresh. Show them only once this work's list has succeeded.
    const isTermsTableLoading = isLoading || (!hasSettledTerms && !termsFailed);
    const showTermsTable = isTermsTableLoading || (hasSettledTerms && workTerms.length > 0);

    useEffect(() => {
        setArticleQuery("");
    }, [work?.id]);

    useEffect(() => {
        if (work?.id == null) return;
        // Idle only: a pending list is in flight, a failed one stays on the empty state instead of retrying.
        if (workTermsWorkId === work.id && workTermsStatus !== "idle") return;
        dispatch(fetchWorkTerms(work.id));
    }, [dispatch, work?.id, workTermsWorkId, workTermsStatus]);

    useEffect(() => {
        if (!usesTitleGroups || titleGroups.length > 0) return;
        dispatch(fetchTitleGroups());
    }, [dispatch, titleGroups.length, usesTitleGroups]);

    const handleAddContract = () => {
        if (!work) return;
        onAddContract(work.id);
    };

    const handleDeleteWork = async () => {
        if (!work) return;
        const titleIds = work.titles.map((title) => title.id);
        const result = await dispatch(deleteWork(work.id));
        if (result.meta.requestStatus === "fulfilled") {
            dispatch(removeTitlesByIds(titleIds));
            onDeleted?.();
        }
    };

    return (
        <section className="work-detail">
            <TabContext value={value}>
                <header className="work-detail__header">
                    <div className="work-detail__header-main">
                        <div className="work-detail__cover">
                            <TitleCover
                                title={showData ? work.name : ""}
                                coverUrl={showData ? coverUrlForWork(work) : undefined}
                                style={{ width: 85, fontSize: "25px" }}
                                color="work-color"
                            />
                        </div>
                        <div className="work-detail__intro">
                            <p className="work-detail__eyebrow">{t("Works.WorkLabel")}</p>
                            <h2 className="work-detail__title">
                                {showData ? work.name : <TextSkeleton width="10em" />}
                            </h2>
                            <p className="work-detail__meta">
                                {showData ? (
                                    t("Works.TitlesCount", { count: titleCount })
                                ) : (
                                    <TextSkeleton width="6em" />
                                )}
                            </p>
                            {(showWorkCode ||
                                showArtworkNature ||
                                showAnalysisCode ||
                                showAssociatedBrand ||
                                showCompensationGroup) && (
                                <div className="work-detail__tags">
                                    {showWorkCode && (
                                        <Tooltip
                                            arrow
                                            title={t("Works.HeaderTagTooltip", {
                                                label: t("Works.WorkCode"),
                                                value: workCode,
                                            })}
                                        >
                                            <span className="work-detail__tag">{workCode}</span>
                                        </Tooltip>
                                    )}
                                    {showArtworkNature && (
                                        <Tooltip
                                            arrow
                                            title={t("Works.HeaderTagTooltip", {
                                                label: t("Works.ArtworkNature"),
                                                value: artworkNatureLabel,
                                            })}
                                        >
                                            <span className="work-detail__tag">{artworkNatureLabel}</span>
                                        </Tooltip>
                                    )}
                                    {showAnalysisCode && (
                                        <Tooltip
                                            arrow
                                            title={t("Works.HeaderTagTooltip", {
                                                label: t("Works.AnalysisCode"),
                                                value: analysisCode,
                                            })}
                                        >
                                            <span className="work-detail__tag">{analysisCode}</span>
                                        </Tooltip>
                                    )}
                                    {showAssociatedBrand && (
                                        <Tooltip
                                            arrow
                                            title={t("Works.HeaderTagTooltip", {
                                                label: t("Works.AssociatedBrand"),
                                                value: associatedBrand,
                                            })}
                                        >
                                            <span className="work-detail__tag">{associatedBrand}</span>
                                        </Tooltip>
                                    )}
                                    {showCompensationGroup && (
                                        <Tooltip
                                            arrow
                                            title={t("Works.HeaderTagTooltip", {
                                                label: t("Works.CompensationGroup"),
                                                value: compensationGroupName,
                                            })}
                                        >
                                            <span className="work-detail__tag">{compensationGroupName}</span>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {(canUpdate || canAddContract) && (
                        <div className="work-detail__header-buttons">
                            {canUpdate && (
                                <ResponsiveButton disabled={!showData} onClick={() => setIsEditOpen(true)}>
                                    <Edit />
                                    <span>{t("Works.EditWork")}</span>
                                </ResponsiveButton>
                            )}
                            {canAddContract && (
                                <ResponsiveButton buttonType="dark" disabled={!showData} onClick={handleAddContract}>
                                    <Plus />
                                    <span>
                                        {workTerms.length > 0
                                            ? t("Beneficiars.AddAContract")
                                            : t("Beneficiars.CreateAContract")}
                                    </span>
                                </ResponsiveButton>
                            )}
                        </div>
                    )}
                </header>

                <div className="work-detail__tabs">
                    <StyledTabs value={value} onChange={handleTabChange} textColor="inherit">
                        <Tab label={t("Works.GeneralInfo")} value="1" />
                        <Tab
                            label={
                                <TabCountLabel
                                    text={t("Works.Contracts")}
                                    count={workTerms.length}
                                    isLoading={isTermsTableLoading}
                                />
                            }
                            value="2"
                        />
                    </StyledTabs>
                </div>

                <TabPanel value="1" className="detail-tab-panel-root">
                    <div
                        className="detail-tab-panel-content"
                        key={getDetailTabPanelContentKey("1", value, tabEnterKey)}
                    >
                        <div className="work-detail__body">
                            <div className="work-detail__section-head">
                                <div>
                                    <h3 className="work-detail__section-label">{t("Works.LinkedTitles")}</h3>
                                    <p className="work-detail__section-hint">
                                        {showData ? (
                                            t("Works.LinkedTitlesHint", { count: titleCount })
                                        ) : (
                                            <TextSkeleton width="14em" />
                                        )}
                                    </p>
                                </div>
                                {canAddTitle && (
                                    <div className="work-detail__section-action">
                                        <ResponsiveButton
                                            disabled={!showData}
                                            onClick={() => work && onAddTitle(work.id)}
                                        >
                                            <Plus />
                                            <span>{t("Works.AddTitle")}</span>
                                        </ResponsiveButton>
                                    </div>
                                )}
                            </div>
                            {showTitleSearch && (
                                <div className="work-detail__title-search">
                                    <FilterInput
                                        filterValue={articleQuery}
                                        setFilterValue={setArticleQuery}
                                        searchPlaceholder={t("Titles.SearchPlaceholder")}
                                        size="small"
                                        fitBtnSize
                                    />
                                </div>
                            )}

                            <div className="work-detail__title-grid">
                                {showData
                                    ? filteredTitles.map((title, index) => (
                                          <WorkTitleCard
                                              key={title.id}
                                              title={title}
                                              index={index}
                                              ean13Label={t("Works.Ean13")}
                                              formatLabel={t("NewTitle.Format")}
                                              openLabel={t("Works.OpenTitle")}
                                              onOpen={onOpenTitle}
                                          />
                                      ))
                                    : [0, 1].map((index) => (
                                          <WorkTitleCard
                                              key={`work-title-skeleton-${index}`}
                                              index={index}
                                              ean13Label={t("Works.Ean13")}
                                              formatLabel={t("NewTitle.Format")}
                                              openLabel={t("Works.OpenTitle")}
                                              onOpen={onOpenTitle}
                                              isLoading
                                          />
                                      ))}
                                {showData && hasArticleQuery && filteredTitles.length === 0 && (
                                    <p className="work-detail__empty-search">{t("Works.NoLinkedTitlesMatch")}</p>
                                )}
                                {canAddTitle && !hasArticleQuery && (
                                    <button
                                        type="button"
                                        className="work-detail__add-card"
                                        disabled={!showData}
                                        onClick={() => work && onAddTitle(work.id)}
                                    >
                                        <span className="work-detail__add-icon">
                                            <Plus />
                                        </span>
                                        <span className="work-detail__add-label">{t("Works.AddTitle")}</span>
                                        <span className="work-detail__add-hint">{t("Works.AddTitleHint")}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </TabPanel>

                <TabPanel value="2" className="detail-tab-panel-root">
                    <div
                        className="detail-tab-panel-content"
                        key={getDetailTabPanelContentKey("2", value, tabEnterKey)}
                    >
                        {showTermsTable ? (
                            <div className="detail-tab-body">
                                <TitlesTerm
                                    currentTitle={titleForTerms}
                                    data={workTerms}
                                    usesMultiPeriod={usesMultiPeriod}
                                    isLoading={isTermsTableLoading}
                                />
                                <StyledButton
                                    sx={{
                                        gap: "8px",
                                        padding: "4px 8px",
                                        background: "white",
                                        color: "#E0DEE0",
                                        width: "100%",
                                    }}
                                    onClick={handleAddContract}
                                    buttonType="secondary"
                                >
                                    <Plus />
                                    {t("Beneficiars.AddAContract")}
                                </StyledButton>
                            </div>
                        ) : (
                            <div className="detail-tab-body">
                                <EmptyState type="generic" sx={{ margin: "56px auto 0" }} />
                                <StyledButton
                                    sx={{
                                        gap: "8px",
                                        padding: "4px 16px",
                                        background: "white",
                                        color: "#E0DEE0",
                                        margin: "auto",
                                        display: "flex",
                                    }}
                                    onClick={handleAddContract}
                                    buttonType="secondary"
                                >
                                    <Plus />
                                    {t("Beneficiars.CreateAContract")}
                                </StyledButton>
                            </div>
                        )}
                    </div>
                </TabPanel>
            </TabContext>

            {canUpdate && work && (
                <NewWorkModal
                    open={isEditOpen}
                    work={work}
                    onClose={() => setIsEditOpen(false)}
                    onRequestDelete={
                        canDelete
                            ? () => {
                                  setIsEditOpen(false);
                                  setIsDeleteOpen(true);
                              }
                            : undefined
                    }
                />
            )}

            {canDelete && work && (
                <DeleteModal
                    open={isDeleteOpen}
                    onClose={() => setIsDeleteOpen(false)}
                    onDelete={handleDeleteWork}
                    elementTitle={work.name}
                    needsDoubleQuotes
                    needsConfirmation
                    confirmationText={t("Works.DeleteWorkConfirmation")}
                    warningMessage={t("Works.DeleteWarning")}
                    description={
                        <>
                            <p>{t("Works.DeleteAssociated")}</p>
                            <ul>
                                <li>{t("Works.DeleteLinkedTitles", { count: titleCount })}</li>
                                <li>{t("Works.DeleteLinkedContracts")}</li>
                                <li>{t("Works.DeleteLinkedData")}</li>
                            </ul>
                        </>
                    }
                />
            )}
        </section>
    );
};

export default WorkDetail;
